import { z } from "zod";
import { GENERATED_LABS } from "./catalogue/labs.generated.ts";

/**
 * The provider catalogue and the operator's allowlist per provider kind (openspec change
 * `model-picking`, D1). A kind names how ESI reaches a lab's models; only kinds ESI has an
 * adapter for can be enabled (Bedrock today; the direct APIs arrive with ADR 0017 slices 3 to 6).
 */

export const PROVIDER_KIND_IDS = [
  "bedrock",
  "anthropic",
  "openai",
  "azure-openai",
  "mistral",
  "openai-compatible",
] as const;
export const providerKindSchema = z.enum(PROVIDER_KIND_IDS);
export type ProviderKind = z.infer<typeof providerKindSchema>;

/** How ESI reaches the provider: Bedrock in an AWS account, or a provider's own HTTP API. */
export type ProviderAccessPath = "bedrock-assume-role" | "bedrock-default" | "direct-api";

export interface ProviderKindInfo {
  readonly id: ProviderKind;
  /** Display name (a product name: not translated). */
  readonly displayName: string;
  readonly accessPaths: readonly ProviderAccessPath[];
  /** The labs (catalogue `lab`) the kind may serve. Empty: any lab its endpoint exposes. */
  readonly labs: readonly string[];
  /** ESI ships an adapter for it: only then can a kind be enabled. */
  readonly adapter: boolean;
}

export const PROVIDER_KINDS: Readonly<Record<ProviderKind, ProviderKindInfo>> = {
  bedrock: {
    id: "bedrock",
    displayName: "Amazon Bedrock",
    accessPaths: ["bedrock-default", "bedrock-assume-role"],
    // The catalogue's served labs (D1 amended by Q18b), generated by `catalogue:sync`.
    labs: Object.keys(GENERATED_LABS),
    adapter: true,
  },
  anthropic: {
    id: "anthropic",
    displayName: "Anthropic API",
    accessPaths: ["direct-api"],
    labs: ["anthropic"],
    adapter: false,
  },
  openai: {
    id: "openai",
    displayName: "OpenAI API",
    accessPaths: ["direct-api"],
    labs: ["openai"],
    adapter: false,
  },
  "azure-openai": {
    id: "azure-openai",
    displayName: "Azure OpenAI",
    accessPaths: ["direct-api"],
    labs: ["openai"],
    adapter: false,
  },
  mistral: {
    id: "mistral",
    displayName: "Mistral API",
    accessPaths: ["direct-api"],
    labs: ["mistral"],
    adapter: false,
  },
  "openai-compatible": {
    id: "openai-compatible",
    displayName: "OpenAI-compatible endpoint",
    accessPaths: ["direct-api"],
    labs: [],
    adapter: false,
  },
};

/** Per kind: whether instance connections may use it, and whether tenants may register their own. */
export const providerAllowanceSchema = z.strictObject({
  instance: z.boolean(),
  tenant: z.boolean(),
});
export type ProviderAllowance = z.infer<typeof providerAllowanceSchema>;

/** `models.providers`: a kind left out is off for both. */
export const modelProvidersSchema = z.partialRecord(providerKindSchema, providerAllowanceSchema);
export type ModelProviders = z.infer<typeof modelProvidersSchema>;

/** The default: the instance's own Bedrock only (D1). */
export const DEFAULT_MODEL_PROVIDERS: ModelProviders = {
  bedrock: { instance: true, tenant: false },
};

/** Whether `providers` allows `kind` for the owner's connections (a missing kind is off). */
export function providerAllowed(
  providers: ModelProviders,
  kind: ProviderKind,
  owner: "instance" | "tenant",
): boolean {
  return providers[kind]?.[owner] === true;
}

/** The kinds `providers` turns on for either owner that ESI has no adapter for. */
export function providersWithoutAdapter(providers: ModelProviders): ProviderKind[] {
  return PROVIDER_KIND_IDS.filter(
    (kind) =>
      !PROVIDER_KINDS[kind].adapter && (providers[kind]?.instance || providers[kind]?.tenant),
  );
}

/** True when `to` turns some owner of some kind on that `from` left off (it widens). */
export function modelProvidersWiden(from: ModelProviders, to: ModelProviders): boolean {
  return PROVIDER_KIND_IDS.some((kind) =>
    (["instance", "tenant"] as const).some(
      (owner) => providerAllowed(to, kind, owner) && !providerAllowed(from, kind, owner),
    ),
  );
}
